import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import {
  Sparkles,
  Send,
  Loader2,
  ShieldCheck,
  X,
  History,
  Plus,
  Trash2,
  ArrowLeft,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useFocusedPatient } from '@/hooks/useFocusedPatient';
import { useShift } from '@/hooks/useShift';
import { useCompany } from '@/hooks/useCompany';
import { useAiAssistant } from '@/hooks/useAiAssistant';
import { getSuggestionKeys, type AiAudience } from '@/lib/ai-suggestions';
import { cn } from '@/lib/utils';

function TypingIndicator({ label }: { label: string }) {
  return (
    <div className="mr-6 flex w-fit items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
      <span className="flex items-center gap-1" aria-hidden="true">
        {[0, 150, 300].map((delay) => (
          <span
            key={delay}
            className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/70 motion-reduce:animate-none"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </span>
      <span className="animate-pulse motion-reduce:animate-none">{label}</span>
    </div>
  );
}

export function AiAssistant() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const { focusedPatient } = useFocusedPatient();
  const { activeShift } = useShift();
  const { isCompanyMember } = useCompany();
  const {
    messages,
    isLoading,
    isRestoring,
    usage,
    sendMessage,
    conversationId,
    hasMoreMessages,
    isLoadingOlder,
    loadOlderMessages,
    conversations,
    hasMoreConversations,
    isLoadingConversations,
    loadConversations,
    openConversation,
    startNewConversation,
    deleteConversation,
  } = useAiAssistant();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [input, setInput] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  const didInitialScrollRef = useRef(false);
  const restoreScrollRef = useRef<number | null>(null);

  const getViewport = () =>
    scrollWrapperRef.current?.querySelector<HTMLElement>('[data-radix-scroll-area-viewport]') ?? null;

  const scrollToBottom = (smooth: boolean) => {
    const viewport = getViewport();
    if (!viewport) return;
    viewport.scrollTo({ top: viewport.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  // Rola para a última mensagem ao abrir o chat (instantâneo) e suavemente nas novas mensagens.
  useEffect(() => {
    if (!open || view !== 'chat') return;

    if (restoreScrollRef.current !== null) {
      const viewport = getViewport();
      if (viewport) viewport.scrollTop = viewport.scrollHeight - restoreScrollRef.current;
      restoreScrollRef.current = null;
      return;
    }

    if (!didInitialScrollRef.current) {
      if (isRestoring || messages.length === 0) return;
      didInitialScrollRef.current = true;
      // Aguarda a animação/layout do Sheet antes de ancorar no fim da lista.
      const frames = [0, 60, 180, 400];
      const timers = frames.map((delay) => window.setTimeout(() => scrollToBottom(false), delay));
      return () => timers.forEach((t) => window.clearTimeout(t));
    }

    scrollToBottom(true);
  }, [open, view, messages, isLoading, isRestoring]);


  useEffect(() => {
    if (!open) {
      didInitialScrollRef.current = false;
      setView('chat');
    }
  }, [open]);

  const audience: AiAudience = isCompanyMember
    ? 'company'
    : activeShift
      ? 'caregiver'
      : 'family';

  const suggestions = useMemo(
    () =>
      getSuggestionKeys({
        route: location.pathname,
        audience,
        hasPatient: !!focusedPatient?.id,
        hasActiveShift: !!activeShift,
      }),
    [location.pathname, audience, focusedPatient?.id, activeShift],
  );

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language?.startsWith('en') ? 'en-US' : 'pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [i18n.language],
  );

  if (!user) return null;

  const screen = {
    route: location.pathname,
    patientId: focusedPatient?.id ?? null,
    shiftId: activeShift?.id ?? null,
  };

  const submit = (text: string) => {
    if (!text.trim() || isLoading) return;
    setInput('');
    void sendMessage(text, screen);
  };

  const showHistory = () => {
    setView('history');
    void loadConversations(true);
  };

  const handleOpenConversation = async (id: string) => {
    await openConversation(id);
    didInitialScrollRef.current = false;
    setView('chat');
  };

  const handleLoadOlder = () => {
    const viewport = getViewport();
    restoreScrollRef.current = viewport ? viewport.scrollHeight - viewport.scrollTop : null;
    void loadOlderMessages();
  };

  return (
    <>
      <Button
        size="icon"
        aria-label={t('aiAssistant.open')}
        onClick={() => setOpen(true)}
        className="fixed bottom-32 right-4 z-40 h-12 w-12 rounded-full shadow-lg md:bottom-28 lg:bottom-6"
      >
        <Sparkles className="h-5 w-5" />
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-md [&>button]:hidden"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <SheetHeader className="border-b p-4">
            <div className="flex items-center justify-between gap-2">
              <SheetTitle className="flex items-center gap-2 text-base">
                <Sparkles className="h-4 w-4 text-primary" />
                {t('aiAssistant.title')}
              </SheetTitle>
              <div className="flex shrink-0 items-center gap-1">
                {view === 'chat' ? (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('aiAssistant.history')}
                      onClick={showHistory}
                      className="h-9 w-9"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('aiAssistant.newConversation')}
                      onClick={startNewConversation}
                      className="h-9 w-9"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    {conversationId && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t('aiAssistant.deleteConversation')}
                        onClick={() => setDeleteTarget(conversationId)}
                        className="h-9 w-9 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('common.back')}
                    onClick={() => setView('chat')}
                    className="h-9 w-9"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('common.close')}
                  onClick={() => setOpen(false)}
                  className="-mr-1 h-9 w-9"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <p className="flex items-center gap-1.5 text-left text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
              {t('aiAssistant.privacyNote')}
            </p>
            {usage && (
              <Badge variant="outline" className="w-fit text-xs font-normal">
                {t('aiAssistant.usage', { used: usage.used, limit: usage.limit })}
              </Badge>
            )}
          </SheetHeader>

          {view === 'history' ? (
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-2">
                {isLoadingConversations && conversations.length === 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('aiAssistant.thinking')}
                  </div>
                )}

                {!isLoadingConversations && conversations.length === 0 && (
                  <p className="text-sm text-muted-foreground">{t('aiAssistant.noConversations')}</p>
                )}

                {conversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    className="flex items-start gap-2 rounded-lg border p-3"
                  >
                    <button
                      type="button"
                      onClick={() => void handleOpenConversation(conversation.id)}
                      className="flex flex-1 items-start gap-2 text-left"
                    >
                      <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs text-muted-foreground">
                          {dateFormatter.format(new Date(conversation.createdAt))}
                        </span>
                        <span className="mt-0.5 block truncate text-sm">
                          {conversation.preview ?? t('aiAssistant.untitledConversation')}
                        </span>
                      </span>
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('aiAssistant.deleteConversation')}
                      onClick={() => setDeleteTarget(conversation.id)}
                      className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                {hasMoreConversations && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={isLoadingConversations}
                    onClick={() => void loadConversations(false)}
                  >
                    {t('aiAssistant.loadOlderConversations')}
                  </Button>
                )}
              </div>
            </ScrollArea>
          ) : (
            <div ref={scrollWrapperRef} className="flex min-h-0 flex-1 flex-col">
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {hasMoreMessages && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                      disabled={isLoadingOlder}
                      onClick={handleLoadOlder}
                    >
                      {isLoadingOlder && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                      {t('aiAssistant.loadOlderMessages')}
                    </Button>
                  )}

                  {isRestoring && messages.length === 0 && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('aiAssistant.thinking')}
                    </div>
                  )}

                  {!isRestoring && messages.length === 0 && (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">{t('aiAssistant.emptyState')}</p>
                      <div className="flex flex-col gap-2">
                        {suggestions.map((key) => (
                          <Button
                            key={key}
                            variant="outline"
                            size="sm"
                            className="h-auto justify-start whitespace-normal py-2 text-left text-sm"
                            onClick={() => submit(t(`aiAssistant.suggestions.${key}`))}
                          >
                            {t(`aiAssistant.suggestions.${key}`)}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        'rounded-lg px-3 py-2 text-sm',
                        message.role === 'user'
                          ? 'ml-6 bg-primary text-primary-foreground'
                          : message.error
                            ? 'mr-6 border border-destructive/30 bg-destructive/10 text-foreground'
                            : 'mr-6 bg-muted text-foreground',
                      )}
                    >
                      {message.role === 'assistant' ? (
                        <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-1 prose-ul:my-1 prose-headings:mt-2">
                          <ReactMarkdown>{message.content}</ReactMarkdown>
                        </div>
                      ) : (
                        message.content
                      )}
                    </div>
                  ))}

                  {isLoading && <TypingIndicator label={t('aiAssistant.thinking')} />}
                  <div ref={bottomRef} />
                </div>
              </ScrollArea>

              <form
                className="flex items-center gap-2 border-t p-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  submit(input);
                }}
              >
                <Input
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder={t('aiAssistant.placeholder')}
                  maxLength={2000}
                  disabled={isLoading}
                />
                <Button type="submit" size="icon" disabled={isLoading || !input.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteTarget} onOpenChange={(next) => !next && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('aiAssistant.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('aiAssistant.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTarget) void deleteConversation(deleteTarget);
                setDeleteTarget(null);
              }}
            >
              {t('aiAssistant.deleteConversation')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
